import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Button, Loader, Menu, Modal, SegmentedControl, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ChevronDown, ChevronRight } from '@untitled-ui/icons-react'
import { Bar } from 'react-chartjs-2'
import 'chart.js/auto'
import dayjs from '@/lib/dayjs'

import ScrollableScreen from '@/components/ui/ScrollableScreen'
import DocumentTitle from '@/components/ui/DocumentTitle'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { SectionTitle } from '@/components/support/ai/PromptBlocks'
import { ModelList, ModelRow, useEvalModels } from '@/components/support/ai/ModelPicker'
import TestChatModal from '@/components/support/ai/TestChatModal'
import {
    splitSections,
    joinSections,
    sameText,
    diffSections,
    diffLines,
    plainLine,
} from '@/lib/policyText'
import EvalRunDetail, { shortModel } from '@/components/support/ai/EvalRunDetail'
import { dollars, barScales, dataset } from '@/components/support/ai/StatTiles'

/* --------------------------------------------------------------- constantes */

const EVALUATIONS_VISIBLE = false

const STYLE_BLOCKS = [
    {
        key: 'email_style',
        title: 'Règles email',
        expect: "Uniquement ce qui est propre à l'email. Inutile d'y répéter un fait déjà écrit plus haut ni une règle qui vaut aussi pour le chat : ce bloc n'apporte que ce qui est nouveau et unique à ce canal.",
    },
    {
        key: 'chat_style',
        title: 'Règles chat',
        expect: "Seulement ce qui est propre au chat, pas de fait ni de règle déjà couverts plus haut ou par l'email.",
    },
]

const TECH_CHANGES = [
    ['email_harness', 'Harness email (technique)'],
    ['chat_harness', 'Harness chat (technique)'],
    ['grounding', 'Grounding (technique)'],
    ['email_model', 'Modèle email'],
    ['chat_model', 'Modèle chat'],
    ['email_reasoning', 'Raisonnement email'],
    ['chat_reasoning', 'Raisonnement chat'],
]

/**
 * Le raisonnement demandé au provider, par agent. `default` laisse le modèle
 * décider (et son défaut varie : Sonnet 5 raisonne fort, Mistral pas du tout),
 * `off` l'éteint explicitement.
 */
const REASONING = [
    { value: 'default', label: 'Défaut du modèle' },
    { value: 'off', label: 'Aucun' },
    { value: 'low', label: 'Léger' },
    { value: 'medium', label: 'Moyen' },
    { value: 'high', label: 'Poussé' },
]
const reasoningLabel = (value) => REASONING.find((r) => r.value === value)?.label ?? value ?? '—'

const STATUS = {
    online: { label: 'En ligne', cls: 'bg-green-50 text-green-700' },
    draft: { label: 'Brouillon', cls: 'bg-orange-50 text-orange-700' },
    archived: { label: 'A servi', cls: 'bg-gray-100 text-gray-500' },
    superseded: { label: 'Remplacée', cls: 'bg-gray-100 text-gray-400' },
}

const KIND_CLASS = {
    added: 'bg-green-50 text-green-700',
    removed: 'bg-red-50 text-red-600',
    changed: 'bg-amber-50 text-amber-700',
}
const KIND = { added: '+', removed: '−', changed: '~' }

const AGENT_LABEL = { email: 'Email', chat: 'Chat' }


/* -------------------------------------------------------------- composants */

const Dots = ({ passed, total }) => (
    <span className="inline-flex items-center gap-1.5">
        <span className="inline-flex gap-0.5" aria-hidden="true">
            {Array.from({ length: Math.min(total, 16) }).map((_, i) => (
                <span key={i} className={`h-2 w-2 rounded-full ${i < passed ? 'bg-green-500' : 'bg-red-200'}`} />
            ))}
        </span>
        <span className="text-[12.5px] font-semibold tabular-nums text-gray-900">
            {passed}/{total}
        </span>
    </span>
)

/** Lecture / édition d'un bloc en modale ; « Appliquer » ne touche que le brouillon local. */
const BlockModal = ({ item, onClose, onApply }) => {
    const [body, setBody] = useState('')
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        setBody(item?.body ?? '')
        setCopied(false)
    }, [item])

    if (!item) return null

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(body)
            setCopied(true)
        } catch {
            notifications.show({ color: 'red', message: 'Copie impossible. Sélectionnez le texte pour le copier.' })
        }
    }

    return <Modal opened onClose={onClose} size="min(1100px, 95vw)" title={item.title}>
        <div className="flex flex-col gap-3">
            <textarea
                aria-label={item.title}
                value={body}
                onChange={event => { setBody(event.currentTarget.value); setCopied(false) }}
                readOnly={!onApply}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                className="h-[65dvh] min-h-64 w-full resize-y rounded border border-gray-300 bg-gray-50 p-4 font-mono text-[13px] leading-relaxed text-gray-900 focus:outline-none focus-visible:bg-white"
            />
            <div className="flex items-center gap-2">
                <Button size="xs" variant="default" onClick={copy}>{copied ? 'Copié' : 'Copier tout'}</Button>
                <div className="ml-auto flex gap-2">
                    <Button size="xs" variant="default" onClick={onClose}>{onApply ? 'Annuler' : 'Fermer'}</Button>
                    {onApply && <Button size="xs" onClick={() => onApply(body)}>Appliquer</Button>}
                </div>
            </div>
        </div>
    </Modal>
}

/** Avant / après d'une section, en texte lisible. */
const CompareModal = ({ change, labels, onClose }) => {
    if (!change) return null
    const lines = diffLines(change.before ?? '', change.after ?? '')
    const side = (kind, drop) => (
        <div className="min-w-0 flex-1">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {kind === 'before' ? labels.before : labels.after}
            </div>
            <div className="flex flex-col rounded-xl border border-gray-200/70 bg-white px-3 py-2 text-[12px] leading-relaxed text-gray-700">
                {lines
                    .filter((l) => l.kind !== drop)
                    .map((l, i) => (
                        <span
                            key={i}
                            className={
                                l.kind === 'added'
                                    ? 'rounded-sm bg-green-50 text-green-800'
                                    : l.kind === 'removed'
                                      ? 'rounded-sm bg-red-50 text-red-700 line-through decoration-red-300'
                                      : ''
                            }
                        >
                            {plainLine(l.text) || ' '}
                        </span>
                    ))}
            </div>
        </div>
    )
    return (
        <Modal
            opened
            onClose={onClose}
            size="xl"
            title={
                <span className="text-sm font-semibold text-gray-900">
                    {change.title}
                    <span className="ml-2 text-xs font-normal text-gray-400">
                        {change.kind === 'added' ? 'ajoutée' : change.kind === 'removed' ? 'supprimée' : 'modifiée'}
                    </span>
                </span>
            }
        >
            <div className="flex max-h-[70vh] gap-4 overflow-y-auto">
                {change.kind !== 'added' && side('before', 'added')}
                {change.kind !== 'removed' && side('after', 'removed')}
            </div>
        </Modal>
    )
}

/**
 * Choix de ce qu'on teste : l'agent, puis le modèle. La liste est curée
 * (`config/ai.php`) et chaque ligne dit ce que ce modèle a donné sur CETTE
 * version et ce que le run coûtera — l'estimation vient du coût par cas
 * réellement facturé la dernière fois, pas d'une grille tarifaire.
 */
const TestModal = ({ version, agents, onClose, onRun }) => {
    const [agent, setAgent] = useState('email')
    const [picked, setPicked] = useState([])
    const data = useEvalModels(version, agent)

    // La configuration de la version est cochée d'office : c'est le test qu'on
    // veut neuf fois sur dix.
    useEffect(() => {
        setPicked(data?.config_model ? [data.config_model] : [])
    }, [data])

    const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
    const estimateOf = (id) => data?.models?.find((m) => m.id === id)?.estimate
    const total = picked.reduce((sum, id) => sum + (estimateOf(id) ?? 0), 0)
    const unknown = picked.some((id) => estimateOf(id) == null)

    return (
        <Modal
            opened
            onClose={onClose}
            size="lg"
            title={<span className="text-sm font-semibold text-gray-900">Tester la version v{version}</span>}
        >
            <div className="flex flex-col gap-4">
                <SegmentedControl
                    size="xs"
                    value={agent}
                    onChange={setAgent}
                    data={Object.entries(AGENT_LABEL).map(([value, label]) => ({
                        value,
                        label: agents[value] ? `${label} · ${agents[value]} cas` : label,
                    }))}
                />

                {!data ? (
                    <div className="flex justify-center py-8">
                        <Loader size={16} />
                    </div>
                ) : data.cases === 0 ? (
                    <p className="py-4 text-[13px] text-gray-500">
                        Cet agent n'a pas encore de cas de test : il n'y a rien à rejouer.
                    </p>
                ) : (
                    <>
                        <ModelList>
                            {data.models.map((m) => (
                                <ModelRow
                                    key={m.id}
                                    model={m}
                                    selected={picked.includes(m.id)}
                                    isConfig={m.id === data.config_model}
                                    onSelect={() => toggle(m.id)}
                                />
                            ))}
                        </ModelList>

                        <div className="flex items-center gap-3">
                            <span className="text-[11px] leading-snug text-gray-400">
                                {picked.length
                                    ? `${picked.length} modèle${picked.length > 1 ? 's' : ''} × ${data.cases} cas ≈ ${dollars(total)}${
                                          unknown ? ' (certains coûts inconnus)' : ''
                                      }`
                                    : 'aucun modèle sélectionné'}
                            </span>
                            <div className="ml-auto flex gap-2">
                                <Button size="xs" variant="default" onClick={onClose}>
                                    Annuler
                                </Button>
                                <Button size="xs" disabled={!picked.length} onClick={() => onRun(picked, agent)}>
                                    Lancer {picked.length > 1 ? `les ${picked.length} tests` : 'le test'}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    )
}

/** Ce qui a changé entre deux configurations, en puces cliquables. */
const Changes = ({ before, after, onOpen }) => {
    const all = useMemo(() => {
        const sections = diffSections(before?.policies, after?.policies)
        const others = [...STYLE_BLOCKS.map((b) => [b.key, b.title]), ...TECH_CHANGES]
            .filter(([key]) => !sameText(before?.[key], after?.[key]))
            .map(([key, title]) => ({ title, kind: 'changed', before: before?.[key], after: after?.[key] }))
        return [...sections, ...others]
    }, [before, after])
    if (!all.length) return <span className="text-[11px] text-gray-400">aucun changement</span>
    return (
        <div className="flex flex-wrap gap-x-4 gap-y-2">
            {all.map((c) => (
                <button
                    key={`${c.kind}-${c.title}`}
                    type="button"
                    onClick={() => onOpen(c)}
                    className="text-sm text-gray-700 underline decoration-gray-300 underline-offset-4 hover:text-gray-900"
                >
                    {c.title}
                </button>
            ))}
        </div>
    )
}

/* ---------------------------------------------------------------- page */

const EMPTY = { policies: '', email_style: '', chat_style: '' }

export default function SupportPolicies() {
    const { requestGet, requestPost } = useAxiosApi()
    const navigate = useNavigate()
    const location = useLocation()
    const { id: idParam } = useParams()

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [shown, setShown] = useState(null) // la version affichée (show)
    const [previous, setPrevious] = useState(null) // la précédente, pour les diffs
    const [usage, setUsage] = useState(null)
    const [texts, setTexts] = useState(EMPTY) // brouillon local (pointe seulement)
    const [saving, setSaving] = useState(false)
    // Un test tourne en FILE (plusieurs minutes) : on suit son avancement au
    // lieu d'attendre une requête HTTP qui serait coupée par le timeout.
    // Un suivi PAR AGENT — { [agent]: { after, expected, models, runs, startedAt } } —
    // pour qu'un test email en cours n'empêche pas d'en lancer un sur le chat,
    // ni d'ajouter un modèle au passage.
    const [pending, setPending] = useState({})
    const pendingRef = useRef(pending)
    const [activating, setActivating] = useState(false)
    const [modal, setModal] = useState(null)
    const [compare, setCompare] = useState(null)
    const [runModal, setRunModal] = useState(null)
    const [modelModal, setModelModal] = useState(null)
    const [testModal, setTestModal] = useState(false)
    const [chatModal, setChatModal] = useState(false)

    /**
     * Démarre — ou étend — le suivi d'un test. Passer par ici et nulle part
     * ailleurs : `expected` conditionne la fin du sondage, et l'oublier laisse
     * la page tourner indéfiniment. Relancer sur le même agent AJOUTE au lot en
     * cours plutôt que d'écraser son décompte.
     */
    const watchTest = (agent, after, expected, models = []) =>
        setPending((p) => {
            const cur = p[agent]
            return {
                ...p,
                [agent]: cur
                    ? { ...cur, expected: cur.expected + expected, models: [...cur.models, ...models] }
                    : { after, expected, models, runs: [], startedAt: Date.now() },
            }
        })

    const load = async () => {
        const { data } = await requestGet('/support/ai/prompt-versions')
        setData(data)
        return data
    }

    useEffect(() => {
        let active = true
        load()
            .then((d) => {
                if (!active) return
                setTexts({ ...EMPTY, ...d.current })
                if (!idParam) navigate(`/support/ai/policies/${d.active_id ?? d.tip_id}`, { replace: true })
            })
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false))
        return () => {
            active = false
        }
    }, [])

    const id = Number(idParam) || data?.active_id
    const versions = data?.versions ?? []
    const row = versions.find((v) => v.id === id)
    const isTip = data && id === data.tip_id

    // La version affichée + sa précédente + son usage.
    useEffect(() => {
        if (!id || !data) return
        let active = true
        setShown(null)
        setPrevious(null)
        setUsage(null)
        requestGet(`/support/ai/prompt-versions/${id}`).then(({ data: v }) => active && setShown(v))
        // La précédente n'est lue que pour montrer ce qu'un BROUILLON change.
        const prev = row?.status === 'draft'
            ? versions.filter((v) => v.id < id).sort((a, b) => b.id - a.id)[0]
            : null
        if (prev) requestGet(`/support/ai/prompt-versions/${prev.id}`).then(({ data: v }) => active && setPrevious(v))
        requestGet(`/support/ai/prompt-versions/${id}/usage`).then(({ data: u }) => active && setUsage(u)).catch(() => {})
        return () => {
            active = false
        }
    }, [id, data?.tip_id, row?.status])

    // Suivi des tests en file : l'état de chaque agent est relu toutes les 3 s
    // jusqu'à ce que tous ses runs soient conclus.
    useEffect(() => {
        pendingRef.current = pending
    }, [pending])

    const agentsWatched = Object.keys(pending).sort().join(',')

    useEffect(() => {
        if (!agentsWatched || !id) return
        let active = true

        const poll = async (agent) => {
            const watched = pendingRef.current[agent]
            if (!watched) return
            const { data: st } = await requestGet(`/support/ai/prompt-versions/${id}/eval-status`, {
                agent,
                after: watched.after,
            })
            if (!active) return
            const runs = st.runs ?? []
            const done = runs.length >= watched.expected && runs.every((r) => r.state !== 'running')

            setPending((p) => {
                if (!p[agent]) return p
                if (done) {
                    const { [agent]: _finished, ...rest } = p
                    return rest
                }
                return {
                    ...p,
                    [agent]: {
                        ...p[agent],
                        runs,
                        // File pleine mais rien qui démarre : pas de worker.
                        stalled: !runs.length && (st.waiting_jobs ?? 0) > 0 && Date.now() - p[agent].startedAt > 20000,
                    },
                }
            })

            if (done) {
                await load()
                // Un seul modèle testé : on ouvre son résultat directement.
                if (runs.length === 1) setRunModal(runs[0].id)
            }
        }

        const tick = () => agentsWatched.split(',').forEach((a) => poll(a).catch(() => {}))
        const timer = setInterval(tick, 3000)
        tick()
        return () => {
            active = false
            clearInterval(timer)
        }
    }, [agentsWatched, id])

    // Restauration d'une section depuis une ancienne version (via location.state).
    useEffect(() => {
        const restore = location.state?.restore
        if (!restore || !data || !isTip) return
        applySection(restore.title, restore.body)
        navigate(location.pathname, { replace: true, state: null })
        notifications.show({ title: `Section « ${restore.title} » reprise`, message: 'Dans le brouillon, à enregistrer.', color: 'blue' })
    }, [location.state, data, isTip])

    const changedKeys = useMemo(() => {
        if (!data || !isTip) return []
        const keys = sameText(data.current?.policies, texts.policies) ? [] : ['Règles communes']
        STYLE_BLOCKS.forEach((b) => {
            if (!sameText(texts[b.key], data.current?.[b.key])) keys.push(b.title)
        })
        return keys
    }, [data, texts, isTip])
    const dirty = changedKeys.length > 0

    // Mémoïsés : Chart.js réanime le graphe dès que `data` ou `options` change
    // d'identité, et cette page se rend à chaque tick du suivi de test.
    const dailyChart = useMemo(() => {
        const days = usage?.email?.daily ?? []
        return {
            data: {
                labels: days.map((d) => dayjs(d.day).format('D MMM')),
                datasets: [
                    dataset('Proposé', days.map((d) => d.send_reply), '#2a78d6'),
                    dataset('Escaladé', days.map((d) => d.none), '#d5dae1'),
                ],
            },
            options: {
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: barScales({ maxTicksLimit: 4 }),
            },
        }
    }, [usage])

    const applySection = (title, body) => {
        const cur = splitSections(texts.policies)
        const i = cur.sections.findIndex((s) => s.title === title)
        const next = [...cur.sections]
        if (i >= 0) next[i] = { title, body }
        else next.push({ title, body })
        setTexts((t) => ({ ...t, policies: joinSections(cur.preamble, next) }))
    }

    /* ---- actions ---- */

    const save = async () => {
        setSaving(true)
        try {
            const { data: res } = await requestPost('/support/ai/prompt-versions', texts)
            const d = await load()
            setTexts({ ...EMPTY, ...d.current })
            navigate(`/support/ai/policies/${res.version.id}`)
            if (res.version.queued) {
                watchTest('email', 0, 1, [d.versions?.find((v) => v.id === res.version.id)?.email_model].filter(Boolean))
                notifications.show({
                    title: `Version v${res.version.id} créée`,
                    message: 'Le test tourne en tâche de fond, le résultat s’affiche ici.',
                    color: 'blue',
                })
            } else {
                notifications.show({
                    title: `Version v${res.version.id} créée`,
                    message: `Test repris d’une version au texte identique : ${res.version.eval.tasks_passed}/${res.version.eval.tasks_total}`,
                    color: 'blue',
                })
            }
        } catch {
            // déjà notifié par useAxiosApi
        } finally {
            setSaving(false)
        }
    }

    const test = async (models = [], agent = 'email') => {
        if (!EVALUATIONS_VISIBLE) return
        const { data: res } = await requestPost(`/support/ai/prompt-versions/${id}/test`, { agent, models })
        setModelModal(null)
        setTestModal(false)
        watchTest(agent, res.after ?? 0, res.queued ?? 1, models)
    }

    const activate = async () => {
        setActivating(true)
        try {
            await requestPost(`/support/ai/prompt-versions/${id}/activate`)
            notifications.show({ title: `Version v${id} en ligne`, message: 'Les deux agents l’utilisent dès maintenant.', color: 'green' })
            const d = await load()
            setTexts({ ...EMPTY, ...d.current })
        } finally {
            setActivating(false)
        }
    }

    const createWithConfig = async (fields) => {
        setSaving(true)
        try {
            const { data: res } = await requestPost('/support/ai/prompt-versions', { ...texts, ...fields })
            const d = await load()
            setTexts({ ...EMPTY, ...d.current })
            navigate(`/support/ai/policies/${res.version.id}`)
        } finally {
            setSaving(false)
            setModelModal(null)
        }
    }

    /* ---- rendu ---- */

    const breadcrumbs = [{ label: 'Support IA' }, { label: 'Fiche de poste' }, ...(row ? [{ label: `v${row.id}` }] : [])]
    const status = row && STATUS[row.status]

    const rightSection = row && (
        <Menu position="bottom-end" width={300}>
            <Menu.Target>
                <Button size="xs" variant="default" rightSection={<ChevronDown width={12} height={12} />}>
                    v{row.id} · {status.label}
                </Button>
            </Menu.Target>
            <Menu.Dropdown>
                {versions.map((v) => {
                    const createdAt = dayjs(v.created_at)
                    const dateLabel = dayjs().diff(createdAt, 'day') < 7
                        ? createdAt.fromNow()
                        : createdAt.format('D MMM YYYY')
                    return (
                        <Menu.Item key={v.id} onClick={() => navigate(`/support/ai/policies/${v.id}`)}>
                            <span className={`flex items-center gap-2 ${v.status === 'superseded' ? 'opacity-40' : ''}`}>
                                <span className="text-[13px] font-semibold text-gray-900">v{v.id}</span>
                                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${STATUS[v.status].cls}`}>
                                    {STATUS[v.status].label}
                                </span>
                                <time dateTime={v.created_at} title={createdAt.format('D MMM YYYY HH:mm')} className="ml-auto whitespace-nowrap text-xs text-gray-500">
                                    {dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}
                                </time>
                            </span>
                        </Menu.Item>
                    )
                })}
            </Menu.Dropdown>
        </Menu>
    )

    if (loading) {
        return (
            <ScrollableScreen breadcrumbs={breadcrumbs}>
                <div className="flex justify-center py-20">
                    <Loader />
                </div>
            </ScrollableScreen>
        )
    }
    if (!data || !row) {
        return (
            <ScrollableScreen breadcrumbs={breadcrumbs}>
                <p className="px-6 py-10 text-gray-400">Version introuvable.</p>
            </ScrollableScreen>
        )
    }

    const compareLabels = { before: `v${previous?.id ?? '?'}`, after: `v${row.id}` }

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            <DocumentTitle title={`BO - Fiche de poste v${row.id} - Baltic Watches`} />

            <main className="@container w-full">
                <div className="mx-auto max-w-3xl space-y-7 p-5 pb-24">
                    {/* ---------- en-tête ---------- */}
                    <header className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <h1 className="text-base font-medium text-gray-900">Fiche de poste</h1>
                            <Tooltip
                                label={<div>Créée le {dayjs(row.created_at).format('D MMM YYYY')}<br />{row.author}{row.activated_at && <><br />Mise en ligne le {dayjs(row.activated_at).format('D MMM YYYY')}</>}</div>}
                                events={{ hover: true, focus: true, touch: true }}
                                multiline
                                maw={320}
                            >
                                <span tabIndex={0} className="cursor-help text-xs text-gray-500">v{row.id} ({status.label.toLowerCase()})</span>
                            </Tooltip>
                        </div>
                        {row.status !== 'online' && <Button size="xs" variant="default" radius="sm" loading={activating} onClick={activate}>Mettre en ligne</Button>}
                    </header>

                    {/* ---------- bandeaux d'état ---------- */}
                    {row.status === 'online' && data.tip_id !== row.id && (
                        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 pb-4">
                            <span className="text-sm text-gray-600">
                                Brouillon disponible : v{data.tip_id}
                            </span>
                            <Button size="xs" variant="default" className="ml-auto" onClick={() => navigate(`/support/ai/policies/${data.tip_id}`)}>
                                Ouvrir la v{data.tip_id}
                            </Button>
                        </div>
                    )}
                    <div className="flex flex-col gap-4">
                        {[{ key: 'policies', title: 'Règles communes' }, ...STYLE_BLOCKS].map(block => {
                            const value = (isTip ? texts[block.key] : shown?.[block.key]) ?? ''
                            const changed = isTip && !sameText(value, data.current?.[block.key])
                            const agent = block.key === 'email_style' ? 'email' : block.key === 'chat_style' ? 'chat' : null
                            const model = agent ? shown?.[`${agent}_model`] : null
                            const reasoning = agent ? shown?.[`${agent}_reasoning`] : null
                            return <section key={block.key} className="flex flex-col gap-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h2 className="text-sm font-medium text-gray-900">{block.title}</h2>
                                    {model && <div className="flex min-w-0 items-center gap-2 text-xs text-gray-500">
                                        <span className="truncate" title={`Raisonnement ${reasoningLabel(reasoning).toLowerCase()}`}>{model}</span>
                                        {isTip && <button type="button" className="shrink-0 underline underline-offset-2 hover:text-gray-900" aria-label={`Modifier le modèle ${AGENT_LABEL[agent]}`} onClick={() => setModelModal({ agent, model, reasoning })}>Modifier</button>}
                                    </div>}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setModal({ key: block.key, title: block.title, body: value })}
                                    className="group flex w-full items-center gap-3 rounded border border-gray-200 bg-white px-4 py-3 text-left hover:bg-gray-50"
                                >
                                    <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-500">{value.length.toLocaleString('fr-FR')} caractères</span>
                                    {changed && <span className="text-xs text-amber-700">Modifié</span>}
                                    <span className="text-xs text-gray-600">{isTip ? 'Ouvrir et modifier' : 'Ouvrir'}</span>
                                    <ChevronRight width={14} height={14} className="shrink-0 text-gray-400" />
                                </button>
                            </section>
                        })}
                    </div>

                    {/* ---------- évaluations ---------- */}
                    {EVALUATIONS_VISIBLE && <section className="flex flex-col gap-2">
                        <SectionTitle
                            hint={`${data.task_counts?.email ?? 0} cas email · ${data.task_counts?.chat ?? 0} cas chat`}
                            action={
                                <div className="flex gap-2">
                                    <Button
                                        size="xs"
                                        variant="default"
                                        disabled={!shown}
                                        onClick={() =>
                                            setChatModal({
                                                model: shown.chat_model,
                                                score: shown.scores?.chat?.gate,
                                            })
                                        }
                                    >
                                        Discuter…
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="light"
                                        onClick={() => setTestModal(true)}
                                    >
                                        Tester…
                                    </Button>
                                </div>
                            }
                        >
                            Évaluations de cette version
                        </SectionTitle>
                        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200/70 bg-white px-5 py-4">
                            {Object.entries(pending).map(([ag, watched]) => {
                                // Les modèles demandés, moins ceux qui ont déjà
                                // un run : le reste est encore en file, et le
                                // dire par son nom vaut mieux que « 1/2 ».
                                const started = new Set(watched.runs.map((r) => r.model))
                                const queued = watched.models.filter((m) => !started.has(m))
                                return (
                                    <div
                                        key={ag}
                                        className="flex flex-col gap-1.5 rounded-xl border border-blue-200/70 bg-blue-50/50 px-3 py-2.5"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <Loader size={14} />
                                            <span className="text-[12.5px] text-gray-700">
                                                Test {AGENT_LABEL[ag].toLowerCase()} —{' '}
                                                {watched.runs.length}/{watched.expected} modèle
                                                {watched.expected > 1 ? 's' : ''}
                                            </span>
                                            <span className="ml-auto text-[11px] text-gray-400">
                                                {watched.stalled
                                                    ? 'aucun worker ne tourne : lancez « php artisan queue:work »'
                                                    : 'vous pouvez quitter la page, il continue'}
                                            </span>
                                        </div>
                                        {watched.runs.map((r) => (
                                            <div key={r.id} className="flex items-center gap-2 pl-6 text-[11.5px] text-gray-500">
                                                <span className="font-mono">{shortModel(r.model)}</span>
                                                <span>
                                                    {r.state === 'done'
                                                        ? `terminé · ${r.tasks_passed}/${r.tasks_total}`
                                                        : r.state === 'interrupted'
                                                          ? 'interrompu'
                                                          : `${r.done}/${r.tasks_total} cas`}
                                                </span>
                                            </div>
                                        ))}
                                        {queued.map((m) => (
                                            <div key={m} className="flex items-center gap-2 pl-6 text-[11.5px] text-gray-400">
                                                <span className="font-mono">{shortModel(m)}</span>
                                                <span>en file d’attente</span>
                                            </div>
                                        ))}
                                    </div>
                                )
                            })}

                            {['email', 'chat'].map((ag) => {
                                // Un run par modèle, le plus récent, classé par
                                // réussite : c'est le classement qu'on vient
                                // chercher. La configuration est DANS la liste,
                                // juste signalée — c'est un modèle comme un autre.
                                const seen = new Set()
                                const rows = (shown?.runs ?? [])
                                    .filter((r) => r.agent === ag && r.state !== 'running')
                                    .filter((r) => !seen.has(r.model) && seen.add(r.model))
                                    .sort(
                                        (a, b) =>
                                            b.tasks_passed / Math.max(1, b.tasks_total) -
                                                a.tasks_passed / Math.max(1, a.tasks_total) ||
                                            new Date(b.started_at) - new Date(a.started_at)
                                    )
                                const configModel = shown?.[`${ag}_model`]

                                return (
                                    <div key={ag} className="flex flex-col gap-1">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                {AGENT_LABEL[ag]}
                                            </span>
                                            <span className="text-[11px] text-gray-300">
                                                {data.task_counts?.[ag] ?? 0} cas
                                            </span>
                                        </div>
                                        {rows.length === 0 ? (
                                            <span className="pl-0.5 text-[12.5px] text-gray-300">
                                                {(data.task_counts?.[ag] ?? 0) > 0
                                                    ? 'pas encore testée'
                                                    : 'pas encore de cas de test'}
                                            </span>
                                        ) : (
                                            <div className="overflow-hidden rounded-xl border border-gray-100">
                                                {rows.map((r) => (
                                                    <button
                                                        key={r.id}
                                                        type="button"
                                                        onClick={() => setRunModal(r.id)}
                                                        className={`flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-0 hover:bg-gray-50/60 ${
                                                            r.model === configModel ? 'bg-blue-50/40' : ''
                                                        }`}
                                                    >
                                                        <span className="w-44 shrink-0 truncate font-mono text-[12px] text-gray-700">
                                                            {shortModel(r.model)}
                                                        </span>
                                                        {r.state === 'interrupted' ? (
                                                            <span className="text-[12px] text-amber-600">interrompu</span>
                                                        ) : (
                                                            <Dots passed={r.tasks_passed} total={r.tasks_total} />
                                                        )}
                                                        {r.model === configModel && (
                                                            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-blue-700">
                                                                configuration
                                                            </span>
                                                        )}
                                                        <span className="ml-auto shrink-0 text-[11px] text-gray-400">
                                                            {r.cost ? `${dollars(r.cost)} · ` : ''}
                                                            {dayjs(r.started_at).fromNow()}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </section>}

                    {/* ---------- usage réel ---------- */}
                    {usage && usage.email.total > 0 && (
                        <section className="flex flex-col gap-2">
                            <SectionTitle hint={usage.email.since ? `depuis le ${dayjs(usage.email.since).format('D MMM YYYY')}` : undefined}>
                                Usage réel de cette version
                            </SectionTitle>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="rounded-2xl border border-gray-200/70 bg-white px-4 py-3">
                                    <div className="text-[11px] text-gray-400">Suggestions email</div>
                                    <div className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900">
                                        {new Intl.NumberFormat('fr-FR').format(usage.email.total)}
                                    </div>
                                    <div className="text-[10.5px] text-gray-400">{usage.email.per_day} par jour</div>
                                </div>
                                <div className="rounded-2xl border border-gray-200/70 bg-white px-4 py-3">
                                    <div className="text-[11px] text-gray-400">Utilisées par l'équipe</div>
                                    <div className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900">
                                        {usage.email.used_rate ?? '—'} <span className="text-sm font-normal text-gray-400">%</span>
                                    </div>
                                    <div className="text-[10.5px] text-gray-400">telles quelles ou retouchées</div>
                                </div>
                                <div className="rounded-2xl border border-gray-200/70 bg-white px-4 py-3">
                                    <div className="text-[11px] text-gray-400">Conversations chat</div>
                                    <div className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900">{usage.chat.conversations}</div>
                                    <div className="text-[10.5px] text-gray-400">
                                        {usage.chat.handover_rate ?? 0} % renvoyées vers l'email
                                    </div>
                                </div>
                            </div>
                            {usage.email.daily.length > 1 && (
                                <div className="rounded-2xl border border-gray-200/70 bg-white px-4 py-3">
                                    <div className="mb-2 flex items-center gap-4">
                                        <span className="text-[13px] font-semibold text-gray-900">Suggestions par jour sous la v{row.id}</span>
                                        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                                            <span className="h-2 w-2 rounded-full" style={{ background: '#2a78d6' }} /> proposé
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                                            <span className="h-2 w-2 rounded-full" style={{ background: '#d5dae1' }} /> escaladé
                                        </span>
                                    </div>
                                    <div className="h-36">
                                        <Bar data={dailyChart.data} options={dailyChart.options} />
                                    </div>
                                </div>
                            )}
                        </section>
                    )}
                    {row.status === 'draft' && previous && shown && (
                        <div className="space-y-3 border-t border-gray-200 pt-5">
                            <h2 className="text-xs font-medium text-gray-500">
                                Modifications depuis v{previous.id}
                            </h2>
                            <Changes before={previous} after={shown} onOpen={setCompare} />
                        </div>
                    )}
                    {row.summary && row.status !== 'draft' && (
                        <p className="whitespace-pre-wrap text-xs leading-relaxed text-gray-500">{row.summary}</p>
                    )}

                </div>

                {/* ---------- barre d'action ---------- */}
                {isTip && (dirty || saving) && (
                    <div className="sticky bottom-0 z-10 border-t border-gray-200/60 bg-gray-50/90 backdrop-blur-xs">
                        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 px-5 py-3">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            <span className="text-xs text-gray-600">
                                {changedKeys.length} bloc{changedKeys.length > 1 ? 's' : ''} modifié{changedKeys.length > 1 ? 's' : ''}
                            </span>
                            <Button size="xs" onClick={save} loading={saving} disabled={!dirty}>
                                Enregistrer et tester
                            </Button>
                            {!saving && (
                                <button
                                    type="button"
                                    onClick={() => setTexts({ ...EMPTY, ...data.current })}
                                    className="text-[11px] text-gray-400 underline decoration-gray-200 underline-offset-2 hover:text-gray-700"
                                >
                                    annuler
                                </button>
                            )}
                            <span className="ml-auto text-[11px] text-gray-400">
                                {saving
                                    ? 'Enregistrement…'
                                    : `l’enregistrement crée la version et lance le test email (${data.task_counts?.email ?? 0} cas)`}
                            </span>
                        </div>
                    </div>
                )}
            </main>

            {/* ---------- modales ---------- */}
            <BlockModal
                item={modal}
                onClose={() => setModal(null)}
                onApply={isTip ? body => {
                    setTexts(current => ({ ...current, [modal.key]: body }))
                    setModal(null)
                } : null}
            />
            <CompareModal change={compare} labels={compareLabels} onClose={() => setCompare(null)} />
            {EVALUATIONS_VISIBLE && chatModal && (
                <TestChatModal
                    version={id}
                    chatModel={chatModal.model}
                    chatScore={chatModal.score}
                    onClose={() => setChatModal(false)}
                />
            )}
            {EVALUATIONS_VISIBLE && testModal && (
                <TestModal
                    version={id}
                    agents={data.task_counts ?? {}}
                    onClose={() => setTestModal(false)}
                    onRun={(models, agent) => test(models, agent)}
                />
            )}
            <Modal opened={!!runModal} onClose={() => setRunModal(null)} size="xl" title={<span className="text-sm font-semibold text-gray-900">Détail du test</span>}>
                {runModal && <EvalRunDetail runId={runModal} compact />}
            </Modal>
            {modelModal && (
                <ConfigModal
                    modal={modelModal}
                    version={id}
                    busy={saving}
                    pending={pending[modelModal.agent]}
                    onClose={() => setModelModal(null)}
                    onCreate={createWithConfig}
                    onTest={test}
                />
            )}
        </ScrollableScreen>
    )
}

/**
 * Changement de la configuration d'un agent : modèle + raisonnement.
 *
 * Le raisonnement est un réglage de la version comme le modèle : un test joué
 * sur cette version l'a été avec le réglage affiché, pas avec celui qu'on
 * s'apprête à choisir. Même liste que la modale de test — un modèle se choisit sur ce qu'il a donné
 * sur CETTE version et sur ce qu'il coûterait par mois au volume réel. Un
 * modèle jamais testé se teste d'ici, sans quitter la modale.
 */
const ConfigModal = ({ modal, version, busy, pending, onClose, onCreate, onTest }) => {
    const [model, setModel] = useState(modal.model ?? '')
    const [reasoning, setReasoning] = useState(modal.reasoning ?? 'default')
    // Rechargée quand un test se termine : les scores apparaissent sans rouvrir.
    const data = useEvalModels(version, modal.agent, !pending)
    const current = data?.models?.find((m) => m.id === model)
    const changed = model && (model !== modal.model || reasoning !== modal.reasoning)

    return (
        <Modal
            opened
            onClose={onClose}
            size="lg"
            title={
                <span className="text-sm font-semibold text-gray-900">
                    Configuration de l’agent {AGENT_LABEL[modal.agent].toLowerCase()}
                </span>
            }
        >
            <div className="flex flex-col gap-4">
                <p className="text-xs leading-relaxed text-gray-500">
                    Le modèle et son raisonnement font partie de la version : en changer crée une nouvelle version, à
                    tester puis mettre en ligne. Le coût mensuel est calculé au volume réel
                    {data?.volume ? ` (${new Intl.NumberFormat('fr-FR').format(data.volume.per_month)} appels par mois)` : ''}.
                </p>

                {!data ? (
                    <div className="flex justify-center py-8">
                        <Loader size={16} />
                    </div>
                ) : (
                    <ModelList>
                        {data.models.map((m) => (
                            <ModelRow
                                key={m.id}
                                model={m}
                                control="radio"
                                selected={model === m.id}
                                isConfig={m.id === data.config_model}
                                configLabel="actuel"
                                onSelect={() => setModel(m.id)}
                                trailing={
                                    !EVALUATIONS_VISIBLE ? null : m.on_version ? (
                                        <span className="w-24 shrink-0 text-right text-[11px] text-gray-400">
                                            {m.monthly != null ? `≈ ${dollars(m.monthly)}/mois` : 'coût inconnu'}
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={!!pending}
                                            onClick={() => onTest([m.id], modal.agent)}
                                            className="w-24 shrink-0 text-right text-[11px] text-baltic underline decoration-blue-200 underline-offset-2 hover:text-baltic-hover disabled:text-gray-300 disabled:no-underline"
                                            title={m.estimate_basis ?? undefined}
                                        >
                                            {pending
                                                ? 'test en cours…'
                                                : `tester${m.estimate != null ? ` ≈ ${dollars(m.estimate)}` : ''}`}
                                        </button>
                                    )
                                }
                            />
                        ))}
                    </ModelList>
                )}

                <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Raisonnement</span>
                    <SegmentedControl size="xs" value={reasoning} onChange={setReasoning} data={REASONING} />
                    <span className="text-[11px] leading-snug text-gray-400">
                        Léger suffit pour choisir un outil et croiser deux contraintes. Poussé double le coût et la
                        latence ; aucun retire le jugement au moment de répondre ou d’escalader. Le coût mensuel
                        ci-dessus est mesuré avec le réglage actuel.
                    </span>
                </div>

                <div className="flex items-center gap-3">
                    <span className="text-[11px] text-gray-400">
                        {current?.on_version
                            ? `${current.on_version.tasks_passed}/${current.on_version.tasks_total} sur cette version${
                                  current.monthly != null ? ` · ≈ ${dollars(current.monthly)} par mois` : ''
                              }`
                            : 'ce modèle n’a pas encore été testé sur cette version'}
                    </span>
                    <div className="ml-auto flex gap-2">
                        <Button size="xs" variant="default" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button
                            size="xs"
                            loading={busy}
                            disabled={!changed}
                            onClick={() =>
                                onCreate({ [`${modal.agent}_model`]: model, [`${modal.agent}_reasoning`]: reasoning })
                            }
                        >
                            Créer la version
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
